import React, { useMemo } from "react"
import { useState } from "react"
import DataTable from "react-data-table-component"
import { customStyles } from "../Common/DataTableStyles"
import { Badge } from "../../../components/ui/badge"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Search, Trash2, Edit2, View, Loader2 } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../components/ui/dropdown-menu"
import { toast } from "react-toastify"
import RoleCreation from "./RoleCreation"
import RoleDetailView from "./UserRoleDetails"


const SAMPLE_PERMISSION_CATEGORIES = [
  {
    id: "1",
    name: "Order Management",
    description: "Order and blockchain management",
    permissions: [
      {
        id: "p1",
        key: "order.create",
        resource: "Order",
        action: "Create",
        description: "Create new orders in the system",
      },
      {
        id: "p2",
        key: "order.read",
        resource: "Order",
        action: "Read",
        description: "View order information",
      },
      {
        id: "p3",
        key: "order.update",
        resource: "Order",
        action: "Update",
        description: "Update order details",
      },
    ],
  },
  {
    id: "2",
    name: "Role Management",
    description: "Role management permissions",
    permissions: [
      {
        id: "p5",
        key: "role.create",
        resource: "Role",
        action: "Create",
        description: "Create new roles",
      },
      {
        id: "p6",
        key: "role.read",
        resource: "Role",
        action: "Read",
        description: "View role information",
      },
      {
        id: "p7",
        key: "role.update",
        resource: "Role",
        action: "Update",
        description: "Update role details and permissions",
      },
    ],
  },
  {
    id: "3",
    name: "Product Types",
    description: "Product type management",
    permissions: [
      {
        id: "p9",
        key: "product.create",
        resource: "Product",
        action: "Create",
        description: "Create new product types",
      },
      {
        id: "p10",
        key: "product.read",
        resource: "Product",
        action: "Read",
        description: "View product types",
      },
    ],
  },
]

interface Permission {
  id: string
  key: string
  resource: string
  action: string
  description: string
}

interface PermissionCategory {
  id: string
  name: string
  description: string
  permissions: Permission[]
}

interface Role {
  id: string
  roleName: string
  description: string
  permissionCategories: PermissionCategory[]
}

interface UserRolesProps {
  roles?: Role[]
  onEditRole?: (role: Role) => void
  onDeleteRole?: (roleId: string) => void
  permissionCategories?: PermissionCategory[]
  onRoleChange?: () => void
}

export default function UserRoles({ roles = [], onEditRole, onDeleteRole, permissionCategories, onRoleChange }: UserRolesProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set())
  const [editingRole, setEditingRole] = useState(null)
  const [viewRole, setViewRole] = useState(null)
  const [isRoleUpdate, setIsRoleUpdate] = useState(false);
  const [isRoleView, setIsRoleView] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(null);

  const filteredRoles = roles.filter(
    (role) =>
      role.roleName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      role.description.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const getTotalPermissions = (role: Role) => {
    return role.permissionCategories.reduce((total, cat) => total + cat.permissions.length, 0)
  }

  const toggleExpandRole = (roleId: string) => {
    const newExpanded = new Set(expandedRoles)
    if (newExpanded.has(roleId)) {
      newExpanded.delete(roleId)
    } else {
      newExpanded.add(roleId)
    }
    setExpandedRoles(newExpanded)
  }

  const handleEditRole = (role) => {
    setIsDropdownOpen(null);
    setEditingRole(role);
    setIsRoleUpdate(true);
  }

  const handleViewRole = (role) => {
    setIsDropdownOpen(null);
    setViewRole(role);
    setIsRoleView(true);
  }

  const handleDeleteRole = (roleId: string, roleName: string) => {
    setIsDropdownOpen(null);
    // if (onDeleteRole) {
    //   onDeleteRole(roleId)
    //   toast.success(`Role "${roleName}" deleted successfully`)
    // }
  }

  const columns = useMemo(() => [
    {
      name: "Role Name",
      selector: (row: Role) => row.roleName,
      sortable: true,
      cell: (row: Role) => (
        <div className="font-medium text-white py-2">{row.roleName}</div>
      ),
    },
    {
      name: "Description",
      selector: (row: Role) => row.description || "—",
      sortable: true,
      cell: (row: Role) => (
        <div className="text-slate-400 py-2">{row.description || "—"}</div>
      ),
    },
    {
      name: "Permissions",
      cell: (row: Role) => (
        <Badge variant="outline" className="border-slate-700 text-slate-300 bg-slate-800">
          {getTotalPermissions(row)} perms
        </Badge>
      ),
      sortable: true,
      selector: (row: Role) => getTotalPermissions(row),
    },
    {
      name: "Categories",
      cell: (row: Role) => (
        <Badge variant="outline" className="border-slate-700 text-slate-300 bg-slate-800">
          {row.permissionCategories.length} categories
        </Badge>
      ),
      sortable: true,
      selector: (row: Role) => row.permissionCategories.length,
    },
    {
      name: "Actions",
      cell: (row: Role, index: number) => (
        <div className="flex justify-end w-full">
          <DropdownMenu modal={false} onOpenChange={(isOpen) => { setIsDropdownOpen(isOpen ? index : null) }} open={isDropdownOpen === index}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white">
                ⋮
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
              <DropdownMenuItem
                className="text-slate-200 cursor-pointer hover:bg-slate-800 focus:bg-slate-800"
                onClick={() => handleEditRole(row)}
              >
                <Edit2 className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-slate-200 cursor-pointer hover:bg-slate-800 focus:bg-slate-800"
                onClick={() => handleViewRole(row)}
              >
                <View className="h-4 w-4 mr-2" /> View
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-400 cursor-pointer hover:bg-slate-800 focus:bg-slate-800"
                onClick={() => handleDeleteRole(row.id, row.roleName)}
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      right: true,
    },
  ], [isDropdownOpen]);

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search roles by name or description..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-black rounded-lg shadow overflow-hidden border border-gray-800">
        <DataTable
          columns={columns}
          data={filteredRoles}
          pagination
          noDataComponent={
            <div className="text-center py-10 text-gray-400">
              {searchQuery
                ? "No roles found matching your search."
                : 'No roles created yet. Click "Create User Role" to get started.'}
            </div>
          }
          customStyles={customStyles}
          highlightOnHover
          pointerOnHover
          responsive
        />
      </div>

      <RoleCreation open={isRoleUpdate} onOpenChange={setIsRoleUpdate} permissionCategories={permissionCategories} editingRole={editingRole} onRoleChange={onRoleChange}/>
      <RoleDetailView open={isRoleView} onOpenChange={setIsRoleView} permissionCategories={permissionCategories} editingRole={viewRole}/>
    </div>
  )
}
